import { supabase } from '../lib/supabase';
import { modulosService } from './modulosService';
import { enviarBroadcastCanalTela } from '../lib/canalTelaBroadcast';
import type { ConfiguracoesTela, ConfiguracoesTelaUpdate, UpdateSettingsBroadcast } from '../models/configuracoes.model';


const BUCKET_LOGOS = 'midias-videos';
const LOGOS_PREFIX = 'logos'; // subpasta dentro do bucket midias-videos
const BUCKET_AUDIO = 'midias-audio';

const DEFAULTS: Omit<ConfiguracoesTela, 'id' | 'tela_id' | 'empresa_id' | 'criado_em' | 'atualizado_em'> = {
  reproducao_aleatoria: false,
  noticias_rodape:      false,
  relogio_digital:      true,
  qrcode_jukebox:       true,
  modal_agora_tocando:  true,
  exibir_clima:         false,
  exibir_futebol:       false,
  exibir_bolao:         true,
  exibir_engajamento_hud: true,
  exibir_pdv_engajamento: true,
  exibir_resultado_eleicao: true,
  exibir_fila_proxima_tv: true,
  exibir_raiox_musica:  false,
  exibir_live_pitch:    false,
  hype_automatico:      true,
  hype_intervalo_min:   45,
  hype_duracao_seg:     15,
  hype_hora_inicio:     '18:00:00',
  hype_hora_fim:        '23:30:00',
  hype_texto:           'Peça sua música!',
  exibir_whatsapp_aviso: false,
  whatsapp_mensagem_prefixo: null,
  exibir_whatsapp_moderacao: false,
  whatsapp_moderadores: null,
  fonte_reproducao:     'playlist',
  audio_captura:        'playlist',
  ganho_captura:        100,
  perfil_audio:         'natural',
  volume_padrao:        100,
  transmissao_fonte_id:          null,
  transmissao_video_id_override: null,
  layout_template:      'fullscreen',
  proporcao_anuncios:   30,
  clima_cidade:         'Goiânia, BR',
  clima_frequencia_min: 60,
  feed_noticias:        'g1-economia',
  feed_rss_custom:      null,
  logos_urls:           [],
  config_batalha_vibes:    false,
  config_invasao_junina:   false,
  config_intervalo_junina: 10,
  config_tema_dia_namorados: false,
  config_tema_festa_junina:  false,
  config_tema_copa:          false,
  momento_especial_audio_fonte:      'padrao',
  momento_especial_audio_url:        null,
  momento_especial_audio_inicio_seg: 0,
  momento_especial_audio_fim_seg:    54,
};

export const configuracoesService = {
  /**
   * Busca as configurações de uma tela.
   * Se ainda não existir, retorna os valores padrão sem persistir.
   */
  async buscar(telaId: string): Promise<ConfiguracoesTela | null> {
    const { data, error } = await supabase
      .from('configuracoes_tela' as never)
      .select('*')
      .eq('tela_id', telaId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    // Mescla defaults para colunas novas ainda ausentes no banco/linha legada
    return { ...DEFAULTS, ...(data as ConfiguracoesTela) } as ConfiguracoesTela;
  },

  /** Reserva global dos anunciantes da plataforma (só leitura para o bar). */
  async buscarReservaAnunciantes(): Promise<number> {
    const { data, error } = await supabase.rpc('reserva_anunciantes_plataforma' as never);
    if (error) throw error;
    return Number(data ?? 0);
  },

  /**
   * Leitura do caminho anon (Player): RPC SECURITY DEFINER por tela_id, sem as colunas
   * de WhatsApp — configuracoes_tela não tem policy de SELECT para anon.
   */
  async buscarParaPlayer(telaId: string): Promise<ConfiguracoesTela | null> {
    const { data, error } = await supabase.rpc('player_buscar_configuracoes' as never, { p_tela_id: telaId } as never);
    if (error) throw error;
    if (!data) return null;
    return { ...DEFAULTS, ...(data as unknown as ConfiguracoesTela) } as ConfiguracoesTela;
  },

  /**
   * Cria ou atualiza as configurações da tela (upsert por tela_id).
   * Após salvar, envia broadcast `update_settings` para o Player da TV.
   */
  async salvar(
    telaId:    string,
    empresaId: string,
    campos:    ConfiguracoesTelaUpdate,
  ): Promise<ConfiguracoesTela> {
    if (campos.qrcode_jukebox === true) {
      // Regra de módulos (plano + exceção da empresa). Sem conseguir ler, o gate fecha, não abre.
      const modulos = await modulosService.meusModulos();
      if (!modulos.includes('jukebox')) {
        throw new Error('QR Code do Jukebox não está disponível no plano atual.');
      }
    }

    const { data, error } = await supabase
      .from('configuracoes_tela' as never)
      .upsert(
        { tela_id: telaId, empresa_id: empresaId, ...campos } as never,
        { onConflict: 'tela_id' },
      )
      .select()
      .single();

    if (error) throw error;
    const cfg = data as ConfiguracoesTela;

    const payload: UpdateSettingsBroadcast = {
      cmd: 'update_settings',
      configuracoes: {
        tela_id:              cfg.tela_id,
        empresa_id:           cfg.empresa_id,
        reproducao_aleatoria: cfg.reproducao_aleatoria,
        noticias_rodape:      cfg.noticias_rodape,
        relogio_digital:      cfg.relogio_digital,
        qrcode_jukebox:       cfg.qrcode_jukebox,
        modal_agora_tocando:  cfg.modal_agora_tocando,
        exibir_clima:         cfg.exibir_clima,
        exibir_futebol:       cfg.exibir_futebol,
        exibir_bolao:         cfg.exibir_bolao,
        exibir_engajamento_hud: cfg.exibir_engajamento_hud,
        exibir_pdv_engajamento: cfg.exibir_pdv_engajamento,
        exibir_resultado_eleicao: cfg.exibir_resultado_eleicao,
        exibir_fila_proxima_tv: cfg.exibir_fila_proxima_tv,
        exibir_raiox_musica:  cfg.exibir_raiox_musica,
        exibir_live_pitch:    cfg.exibir_live_pitch,
        hype_automatico:      cfg.hype_automatico,
        hype_intervalo_min:   cfg.hype_intervalo_min,
        hype_duracao_seg:     cfg.hype_duracao_seg,
        hype_hora_inicio:     cfg.hype_hora_inicio,
        hype_hora_fim:        cfg.hype_hora_fim,
        hype_texto:           cfg.hype_texto,
        exibir_whatsapp_aviso: cfg.exibir_whatsapp_aviso,
        exibir_whatsapp_moderacao: cfg.exibir_whatsapp_moderacao,
        fonte_reproducao:    cfg.fonte_reproducao,
        audio_captura:        cfg.audio_captura,
        ganho_captura:        cfg.ganho_captura,
        perfil_audio:         cfg.perfil_audio,
        volume_padrao:        cfg.volume_padrao,
        transmissao_fonte_id:          cfg.transmissao_fonte_id,
        transmissao_video_id_override: cfg.transmissao_video_id_override,
        layout_template:      cfg.layout_template,
        proporcao_anuncios:   cfg.proporcao_anuncios,
        clima_cidade:         cfg.clima_cidade,
        clima_frequencia_min: cfg.clima_frequencia_min,
        feed_noticias:        cfg.feed_noticias,
        feed_rss_custom:      cfg.feed_rss_custom,
        logos_urls:              cfg.logos_urls,
        config_batalha_vibes:    cfg.config_batalha_vibes,
        config_invasao_junina:   cfg.config_invasao_junina,
        config_intervalo_junina: cfg.config_intervalo_junina,
        config_tema_dia_namorados: cfg.config_tema_dia_namorados,
        config_tema_festa_junina:  cfg.config_tema_festa_junina,
        config_tema_copa:          cfg.config_tema_copa,
        momento_especial_audio_fonte:      cfg.momento_especial_audio_fonte,
        momento_especial_audio_url:        cfg.momento_especial_audio_url,
        momento_especial_audio_inicio_seg: cfg.momento_especial_audio_inicio_seg,
        momento_especial_audio_fim_seg:    cfg.momento_especial_audio_fim_seg,
      },
    };

    await enviarBroadcastCanalTela(telaId, 'cmd', payload as unknown as Record<string, unknown>);

    return cfg;
  },

  /**
   * Faz upload de uma logomarca para o bucket `midias-videos/logos/{empresaId}/`.
   * Retorna a URL pública do arquivo.
   */
  async uploadLogo(
    empresaId:  string,
    file:       File,
    onProgress: (pct: number) => void,
  ): Promise<string> {
    const ext  = file.name.split('.').pop() ?? 'png';
    const nome = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const path = `${LOGOS_PREFIX}/${empresaId}/${nome}`;

    const { error } = await supabase.storage
      .from(BUCKET_LOGOS)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        // @ts-expect-error — onUploadProgress suportado mas não tipado no SDK
        onUploadProgress: (ev: { loaded: number; total: number }) => {
          onProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });

    if (error) throw error;

    const { data } = supabase.storage.from(BUCKET_LOGOS).getPublicUrl(path);
    return data.publicUrl;
  },

  /**
   * Faz upload de um áudio para o bucket `midias-audio/{empresaId}/`.
   * Retorna a URL pública do arquivo. Compartilhado entre Momento Especial e
   * Identidade Sonora — mesmo bucket, mesma convenção de path.
   */
  async uploadAudioBucket(
    empresaId:  string,
    file:       File,
    onProgress: (pct: number) => void,
  ): Promise<string> {
    const ext  = file.name.split('.').pop() ?? 'mp3';
    const nome = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const path = `${empresaId}/${nome}`;

    const { error } = await supabase.storage
      .from(BUCKET_AUDIO)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        // @ts-expect-error — onUploadProgress suportado mas não tipado no SDK
        onUploadProgress: (ev: { loaded: number; total: number }) => {
          onProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });

    if (error) throw error;

    const { data } = supabase.storage.from(BUCKET_AUDIO).getPublicUrl(path);
    return data.publicUrl;
  },

  uploadMomentoEspecialAudio(
    empresaId:  string,
    file:       File,
    onProgress: (pct: number) => void,
  ): Promise<string> {
    return this.uploadAudioBucket(empresaId, file, onProgress);
  },

  /**
   * Remove uma logomarca do bucket midias-videos e retorna a lista atualizada de URLs.
   */
  async removerLogo(url: string, logosAtuais: string[]): Promise<string[]> {
    const path = url.split(`/${BUCKET_LOGOS}/`)[1];
    if (path) {
      // Se o arquivo não saiu do Storage a logo não pode sumir da lista (viraria órfão invisível).
      const { error } = await supabase.storage.from(BUCKET_LOGOS).remove([path]);
      if (error) throw error;
    }
    return logosAtuais.filter(u => u !== url);
  },

  /** Retorna os valores padrão para uso na inicialização do formulário */
  defaults() {
    return { ...DEFAULTS };
  },

};
